import type { ReactNode } from 'react';

import { LinkButton, TextLink } from '@/components/ui/link';
import { canOpen, type NavTarget } from '@/lib/nav';
import { useViewerRole } from '@/lib/viewer';

/**
 * 画面間のリンク。
 *
 * 飛び先を**開ける人にだけ**リンクとして出す。開けない人にリンクを見せると、
 * 押した先でリダイレクトされて「壊れている」ように見える
 * (`mcp` ロールは管理画面のほとんどを開けない)。
 * 判定は `lib/nav.ts` の経路の表 1 箇所。画面ごとに書かない。
 */

/** エンティティの名前そのものをリンクにする。開けない人にはただの文字列。 */
export function EntityLink({
  target,
  className,
  children,
}: {
  target: NavTarget;
  className?: string;
  children: ReactNode;
}) {
  const role = useViewerRole();
  // 開けない人にも見た目 (truncate や字送り) は要るので、className は同じく当てる
  if (!canOpen(role, target)) return <span className={className}>{children}</span>;
  return (
    <TextLink to={target.to} className={className}>
      {children}
    </TextLink>
  );
}

/**
 * 行末の「次の画面へ」。開けない人には何も出さない。
 * 表示名は経路の表の label が既定なので、サイドバーと呼び名がずれない。
 */
export function NavAction({
  target,
  className,
  children,
}: {
  target: NavTarget;
  className?: string;
  children?: ReactNode;
}) {
  const role = useViewerRole();
  if (!canOpen(role, target)) return null;
  return (
    <LinkButton to={target.to} className={className}>
      {children ?? target.label}
    </LinkButton>
  );
}
